import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteFirewall } from "../../api/endpoints/siteFirewall";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import type { FirewallRule, RuleCreateIn } from "../../api/types/siteFirewall";
import { Table, type Column } from "../../components/ui/Table";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { FirewallRuleForm } from "./FirewallRuleForm";
import { useNetworkDefaults } from "./useNetworkDefaults";
import { GeneratedFromAllocationBanner } from "./GeneratedFromAllocationBanner";

export function FirewallRulesTable({
  accountSlug,
  siteSlug,
  canWrite,
}: {
  accountSlug: string;
  siteSlug: string;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<FirewallRule | null>(null);
  const [adding, setAdding] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyAttempt, setApplyAttempt] = useState(0);
  function openApply() {
    setApplyAttempt((n) => n + 1); // fresh proposal each time the modal opens
    setApplying(true);
  }

  const q = useQuery({
    queryKey: qk.siteFirewallRules(siteSlug),
    queryFn: () => siteFirewall.listRules(siteSlug).then((r) => r.data),
  });
  // Standard-rules proposal, fetched only when the operator opens "Apply".
  const defaults = useNetworkDefaults(siteSlug, { enabled: applying, attempt: applyAttempt });
  const apply = useMutation({
    mutationFn: (rules: RuleCreateIn[]) =>
      Promise.allSettled(rules.map((rule) => siteFirewall.createRule(siteSlug, rule))),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteFirewallRules(siteSlug) });
      void qc.invalidateQueries({ queryKey: qk.siteFirewallPolicy(siteSlug) });
    },
  });
  function closeApply() {
    setApplying(false);
    apply.reset();
  }

  const del = useMutation({
    mutationFn: (id: string) => siteFirewall.deleteRule(siteSlug, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteFirewallRules(siteSlug) });
      void qc.invalidateQueries({ queryKey: qk.siteFirewallPolicy(siteSlug) });
    },
  });

  const columns: ReadonlyArray<Column<FirewallRule>> = [
    { key: "family", header: "Family", render: (r) => <FamilyBadge family={r.family} /> },
    { key: "name", header: "Name", render: (r) => <code>{r.name}</code> },
    {
      key: "route",
      header: "From → To",
      render: (r) => `${r.from_vlan_name} → ${r.to_vlan_name ?? "WAN"}`,
    },
    { key: "protocol", header: "Protocol", render: (r) => r.protocol ?? "any" },
    {
      key: "dst_ports",
      header: "Ports",
      render: (r) => (r.dst_ports.length ? r.dst_ports.join(", ") : "—"),
    },
    { key: "notes", header: "Notes", render: (r) => r.notes ?? "—" },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) =>
        canWrite ? (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button type="button" style={linkBtn} onClick={() => setEditing(r)}>
              Edit
            </button>
            <button
              type="button"
              style={dangerLinkBtn}
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(`Delete firewall rule ${r.family}/${r.name}?`)) del.mutate(r.id);
              }}
            >
              Delete
            </button>
          </span>
        ) : null,
    },
  ];

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading firewall rules…</p>;
  if (q.error) return <ErrorBanner error={q.error} title="Failed to load firewall rules" />;

  const rules = [...(q.data ?? [])].sort(
    (a, b) => a.family.localeCompare(b.family) || a.name.localeCompare(b.name),
  );

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Firewall rules</h3>
        {canWrite && (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button type="button" style={secondaryBtn} onClick={openApply}>
              Apply standard rules
            </button>
            <button type="button" style={primaryBtn} onClick={() => setAdding(true)}>
              Add rule
            </button>
          </span>
        )}
      </div>

      {del.error && <ErrorBanner error={del.error} title="Delete failed" />}

      <Table<FirewallRule>
        columns={columns}
        rows={rules}
        rowKey={(r) => r.id}
        empty={
          <EmptyState
            title="No firewall rules"
            description="Inter-VLAN and outbound allow-rules layered on the default policy."
          />
        }
      />

      <Modal
        open={adding || editing !== null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        title={editing ? "Edit firewall rule" : "Add firewall rule"}
      >
        <FirewallRuleForm
          accountSlug={accountSlug}
          siteSlug={siteSlug}
          rule={editing ?? undefined}
          canWrite={canWrite}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      </Modal>

      <Modal open={applying} onClose={closeApply} title="Apply standard firewall rules">
        {defaults.isPending && (
          <p style={{ color: "var(--text-dim)" }}>Computing standard rules from allocation…</p>
        )}
        {defaults.error && (
          <ErrorBanner error={defaults.error} title="Can't compute standard rules yet" />
        )}
        {defaults.data && (
          <ApplyStandardRules
            rules={defaults.data.firewall_rules}
            source={defaults.data.source}
            result={apply.data ?? null}
            pending={apply.isPending}
            onApply={() => apply.mutate(defaults.data!.firewall_rules)}
            onClose={closeApply}
          />
        )}
      </Modal>
    </div>
  );
}

function ApplyStandardRules({
  rules,
  source,
  result,
  pending,
  onApply,
  onClose,
}: {
  rules: RuleCreateIn[];
  source: { vendor_profile?: string; generator_version?: string };
  result: PromiseSettledResult<unknown>[] | null;
  pending: boolean;
  onApply: () => void;
  onClose: () => void;
}) {
  if (result) {
    const created = result.filter((r) => r.status === "fulfilled").length;
    const skipped = result.filter(
      (r) => r.status === "rejected" && r.reason instanceof ApiError && r.reason.status === 409,
    ).length;
    const failed = result.length - created - skipped;
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p>
          Created <strong>{created}</strong>, skipped <strong>{skipped}</strong> (already existed)
          {failed > 0 ? <>, <span style={{ color: "var(--crit)" }}>failed {failed}</span></> : null}.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" style={primaryBtn} onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <GeneratedFromAllocationBanner source={source} />
      <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-dim)" }}>
        {rules.length} proposed rule(s). Existing rules with the same name are skipped.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.85rem", display: "grid", gap: 2 }}>
        {rules.map((r) => (
          <li key={`${r.family}/${r.name}`}>
            <code>{r.name}</code> — {r.from_vlan_name} → {r.to_vlan_name ?? "WAN"}
          </li>
        ))}
      </ul>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" style={secondaryBtn} disabled={pending} onClick={onClose}>Cancel</button>
        <button type="button" style={primaryBtn} disabled={pending} onClick={onApply}>
          {pending ? "Applying…" : `Apply ${rules.length} rule(s)`}
        </button>
      </div>
    </div>
  );
}

function FamilyBadge({ family }: { family: string }) {
  const label = family === "inter_vlan" ? "inter-VLAN" : "outbound";
  return (
    <span
      style={{
        fontSize: "0.72rem",
        padding: "2px 6px",
        borderRadius: 4,
        border: "1px solid var(--border)",
        color: "var(--text-dim)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text-dim)",
  border: "1px solid var(--border)",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: "0.85rem",
  cursor: "pointer",
};

const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--accent)",
  cursor: "pointer",
  fontSize: "0.85rem",
  padding: 0,
};

const dangerLinkBtn: React.CSSProperties = {
  ...linkBtn,
  color: "var(--crit)",
};
